const DEFAULT_WAIT_MS = 500;
const HIGH_ENTROPY_HINTS = [
    'platformVersion',
    'model',
    'formFactors',
    'architecture',
    'bitness',
    'fullVersionList',
];

function normalizeString(value) {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function normalizeStringArray(value) {
    if (!Array.isArray(value)) {
        return null;
    }

    const normalized = value
        .map(normalizeString)
        .filter(item => item !== null);

    return normalized.length > 0 ? normalized : null;
}

function normalizeBrandVersions(value) {
    if (!Array.isArray(value)) {
        return null;
    }

    const normalized = value.reduce((result, item) => {
        const brand = normalizeString(item?.brand);
        const version = normalizeString(item?.version);
        if (brand !== null && version !== null) {
            result.push({brand, version});
        }
        return result;
    }, []);

    return normalized.length > 0 ? normalized : null;
}

async function getHighEntropyValues(window, userAgentData, waitMs, timersReady) {
    if (typeof userAgentData.getHighEntropyValues !== 'function') {
        return null;
    }

    const setTimer = window?.setTimeout?.bind(window) || setTimeout;
    const clearTimer = window?.clearTimeout?.bind(window) || clearTimeout;
    let timeoutId;
    let settled = false;
    const timeout = new Promise(resolve => {
        // Запрос уходит сразу, а таймаут отсчитывается после сигнала, когда главный поток свободен.
        timersReady.then(() => {
            if (!settled) {
                timeoutId = setTimer(() => resolve(null), waitMs);
            }
        });
    });

    try {
        return await Promise.race([
            userAgentData.getHighEntropyValues(HIGH_ENTROPY_HINTS),
            timeout,
        ]);
    } catch (error) {
        void error;
        return null;
    } finally {
        settled = true;
        if (typeof timeoutId !== 'undefined') {
            clearTimer(timeoutId);
        }
    }
}

/**
 * Собирает данные об ОС, типе устройства и браузере через User-Agent Client Hints.
 * @param {Window} window
 * @param {number} waitMs
 * @param {Promise<void>} timersReady Таймаут начинает отсчёт после этого сигнала (см. fingerprint.js).
 * @returns {Promise<IUserAgentDataContainer>}
 */
export default async function collectUserAgentData(window, waitMs = DEFAULT_WAIT_MS, timersReady = Promise.resolve()) {
    const userAgentData = window?.navigator?.userAgentData;
    if (!userAgentData || typeof userAgentData !== 'object') {
        return {uaData: null};
    }

    const highEntropy = await getHighEntropyValues(window, userAgentData, waitMs, timersReady);

    return {
        uaData: {
            platform: normalizeString(userAgentData.platform),
            platformVersion: normalizeString(highEntropy?.platformVersion),
            model: normalizeString(highEntropy?.model),
            mobile: typeof userAgentData.mobile === 'boolean' ? userAgentData.mobile : null,
            formFactors: normalizeStringArray(highEntropy?.formFactors),
            architecture: normalizeString(highEntropy?.architecture),
            bitness: normalizeString(highEntropy?.bitness),
            fullVersionList: normalizeBrandVersions(highEntropy?.fullVersionList),
        },
    };
}
